/**
 * /challenges/:id — screen 3m.
 *
 * Eight `status` values drive this view (D61), and all eight are here. `push`
 * and `void` are *outcomes*, not statuses: they show inside `resolved` and
 * `settled` as "nobody owes anything" rather than as a winner.
 *
 * Two blocks below the fold do the work: STATUS (what happened, and the 12-hour
 * dispute window, D62) and SETTLEMENT (who owes what, marked per participant,
 * D68 — a three-person side settles at three different times).
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import type { Challenge, ChallengeParticipant, ChallengeOutcome } from '@shared/types'
import { describeLock } from '@shared/templates'
import { useStore } from '../lib/store'
import { ApiFailure, GET, writeOrQueue } from '../lib/api'
import {
  Body, Button, ButtonPair, Card, Eyebrow, Header, Notice, Screen, SkeletonCard, Spacer,
} from '../ui'

// ── formatting ──────────────────────────────────────────────────────────────

const STAMP = new Intl.DateTimeFormat(undefined, {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
})
const DAY_TIME = new Intl.DateTimeFormat(undefined, {
  weekday: 'short', hour: 'numeric', minute: '2-digit', hour12: true,
})

/** "11h 24m" — the dispute window and the lock countdown both read this way. */
function countdown(ms: number): string {
  if (ms <= 0) return 'any moment'
  const mins = Math.floor(ms / 60_000)
  const days = Math.floor(mins / 1440)
  const hours = Math.floor((mins % 1440) / 60)
  const rem = mins % 60
  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${rem}m`
  return `${Math.max(1, rem)}m`
}

function nameOf(p: ChallengeParticipant, meId: string): string {
  return p.userId && p.userId === meId ? 'You' : p.displayName
}

function namesOf(side: ChallengeParticipant[], meId: string): string {
  if (!side.length) return '-'
  return side.map((p) => nameOf(p, meId)).join(' + ')
}

function participantState(p: ChallengeParticipant, status: Challenge['status']): string {
  if (p.declinedAt) return 'declined'
  if (p.acceptedAt) return 'accepted'
  if (p.isFreeform) return 'not on the app'
  if (status === 'proposed') return 'hasn’t answered'
  return ''
}

function useTick(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs)
    return () => clearInterval(t)
  }, [intervalMs])
  return now
}

// ── screen ──────────────────────────────────────────────────────────────────

export default function ChallengeDetail() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { state, toast, refresh } = useStore()
  const meId = state?.user.id ?? ''
  const now = useTick()

  const [challenge, setChallenge] = useState<Challenge | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [fatal, setFatal] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [disputeOpen, setDisputeOpen] = useState(false)
  const [disputeNote, setDisputeNote] = useState('')

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const res = await GET<{ challenge: Challenge }>(`/api/challenges/${id}`, signal)
      setChallenge(res.challenge)
      setErr(null)
      setFatal(null)
    } catch (e) {
      if (signal?.aborted) return
      if (e instanceof ApiFailure && (e.code === 'not_found' || e.code === 'forbidden')) {
        setFatal('That challenge isn’t here. It may be private, or it may never have existed.')
      } else {
        setErr(e instanceof ApiFailure ? e.message : 'Couldn’t refresh this challenge.')
      }
    } finally {
      if (!signal?.aborted) setLoaded(true)
    }
  }, [id])

  useEffect(() => {
    const ac = new AbortController()
    void load(ac.signal)
    return () => ac.abort()
  }, [load])

  const post = useCallback(async (path: string, body: unknown, label: string) => {
    setBusy(true)
    try {
      const res = await writeOrQueue(`/api/challenges/${id}/${path}`, 'POST', body, label)
      if (res === null) toast('Saved. It will send when you’re back online')
      await load()
      void refresh(true)
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : 'Couldn’t record that.')
    } finally {
      setBusy(false)
    }
  }, [id, load, refresh, toast])

  const c = challenge

  const mySide = c?.viewer.side ?? null
  const leftSide = mySide === 'b' ? 'b' : 'a'
  const rightSide = leftSide === 'a' ? 'b' : 'a'

  const winners = useMemo(() => {
    if (!c || (c.outcome !== 'side_a' && c.outcome !== 'side_b')) return []
    return c.outcome === 'side_a' ? c.sides.a : c.sides.b
  }, [c])
  const losers = useMemo(() => {
    if (!c || (c.outcome !== 'side_a' && c.outcome !== 'side_b')) return []
    return c.outcome === 'side_a' ? c.sides.b : c.sides.a
  }, [c])

  return (
    <Screen style={{ flex: '1 1 auto', minHeight: 0 }}>
      <Header
        bordered={false}
        logo={false}
        left={
          <button
            onClick={() => navigate('/challenges')}
            aria-label="Back to challenges"
            style={{ fontSize: 15, fontWeight: 700, minHeight: 44, textAlign: 'left' }}
          >
            ← Challenge
          </button>
        }
      />

      <Body style={{ padding: '10px 18px calc(24px + var(--nav-h))', textAlign: 'center' }}>
        {!loaded ? (
          <>
            <SkeletonCard lines={3} />
            <Spacer h={12} />
            <SkeletonCard lines={4} />
          </>
        ) : fatal ? (
          <Card style={{ padding: '26px 18px' }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Nothing to see here</div>
            <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 6 }}>{fatal}</div>
            <Spacer h={14} />
            <Button variant="secondary" onClick={() => navigate('/challenges')}>Back to challenges</Button>
          </Card>
        ) : !c ? null : (
          <>
            {err ? <><Notice tone="warn">{err}</Notice><Spacer h={14} /></> : null}

            <div
              style={{
                fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em', textWrap: 'balance',
                textDecoration: c.status === 'voided' ? 'line-through' : undefined,
                opacity: c.status === 'voided' || c.status === 'expired' ? 0.7 : 1,
              }}
            >
              “{c.proposition}”
            </div>
            <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 8 }}>
              {[c.gameLabel, c.seasonYear ? `${c.seasonYear} season` : null].filter(Boolean).join(' · ') || 'No game'}
            </div>

            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 22 }}>
              <SideCard
                side={leftSide}
                people={c.sides[leftSide]}
                status={c.status}
                meId={meId}
                ringed={mySide === leftSide}
              />
              <div style={{ fontSize: 13, color: 'var(--dim)', flex: '0 0 auto' }}>vs</div>
              <SideCard
                side={rightSide}
                people={c.sides[rightSide]}
                status={c.status}
                meId={meId}
                ringed={mySide === rightSide}
              />
            </div>

            <div style={{ fontSize: 17, fontWeight: 700, marginTop: 20 }}>{c.stakeLabel}</div>

            <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 10, lineHeight: 1.7 }}>
              {c.locksAt !== null ? (
                <>
                  Locks: {describeLock(c.templateId, c.params).replace(/^Locks\s+/i, '').replace(/\.$/, '')}
                  {' · '}{DAY_TIME.format(c.locksAt)}
                  <br />
                </>
              ) : (
                <>No automatic lock<br /></>
              )}
              Created by {c.creatorName} · {STAMP.format(c.createdAt)} · {c.isPublic ? 'Public' : 'Private'}
            </div>

            {/* ── STATUS ─────────────────────────────────────────────────── */}
            <Block title="Status">
              <StatusBlock
                challenge={c}
                meId={meId}
                now={now}
                busy={busy}
                onAccept={() => void post('accept', {}, 'Accepted a challenge')}
                onDecline={() => void post('decline', {}, 'Declined a challenge')}
                onConfirm={() => void post('confirm', {}, 'Confirmed an outcome')}
                onDispute={() => setDisputeOpen(true)}
                onResolve={(outcome) => void post('resolve', { outcome }, 'Called an outcome')}
                onVoid={() => void post('void', {}, 'Voided a challenge')}
                onProposeAgain={() => navigate(`/challenges/new${c.gameId ? `?game=${c.gameId}` : ''}`)}
              />

              {disputeOpen ? (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 12, color: 'var(--dim)', marginBottom: 6 }}>
                    What do you think happened? (optional)
                  </div>
                  <textarea
                    value={disputeNote}
                    onChange={(e) => setDisputeNote(e.currentTarget.value)}
                    maxLength={500}
                    rows={3}
                    aria-label="Why you disagree"
                    style={{
                      width: '100%', padding: '13px 14px', borderRadius: 12,
                      border: '1px solid var(--line)', background: 'var(--surf)',
                      fontSize: 14, color: 'var(--fg)', resize: 'vertical',
                    }}
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <Button
                      compact
                      disabled={busy}
                      onClick={() => {
                        setDisputeOpen(false)
                        void post('dispute', { note: disputeNote.trim() || undefined }, 'Disputed an outcome')
                      }}
                    >
                      Send dispute
                    </Button>
                    <Button compact variant="secondary" onClick={() => setDisputeOpen(false)}>Cancel</Button>
                  </div>
                </div>
              ) : null}
            </Block>

            {/* ── SETTLEMENT ─────────────────────────────────────────────── */}
            {losers.length ? (
              <Block title="Settlement">
                <div style={{ fontSize: 15, marginTop: 10 }}>
                  {namesOf(losers, meId)} {losers.length === 1 && losers[0].userId !== meId ? 'owes' : 'owe'}{' '}
                  {namesOf(winners, meId)} {c.stakeQuantity} {c.stakeUnit}
                  {c.stakeMode === 'split' ? ' (split across the losing side)' : ''}
                </div>

                {losers.map((p) => (
                  <SettleRow
                    key={p.id}
                    person={p}
                    meId={meId}
                    busy={busy}
                    canMark={c.viewer.isParticipant && (p.userId === meId || c.viewer.won === true)}
                    onMark={() => void post('settle', { participantId: p.id }, 'Marked a stake done')}
                  />
                ))}
              </Block>
            ) : null}
          </>
        )}
      </Body>
    </Screen>
  )
}

// ── pieces ──────────────────────────────────────────────────────────────────

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ marginTop: 22, paddingTop: 20, borderTop: '1px solid var(--line)', textAlign: 'left' }}>
      <Eyebrow>{title}</Eyebrow>
      {children}
    </div>
  )
}

function SideCard({
  side, people, status, meId, ringed,
}: {
  side: 'a' | 'b'
  people: ChallengeParticipant[]
  status: Challenge['status']
  meId: string
  ringed: boolean
}) {
  return (
    <div
      style={{
        flex: 1, minWidth: 0, textAlign: 'center',
        background: 'var(--surf)',
        border: `1px solid ${ringed ? 'var(--fill)' : 'var(--line)'}`,
        borderRadius: 'var(--radius-tile)',
        padding: 16,
      }}
    >
      {/* D113 — the proposition is side A's claim, so the sides read as positions. */}
      <Eyebrow style={{ marginBottom: 8 }}>{side === 'a' ? 'Says yes' : 'Says no'}</Eyebrow>
      {people.length === 0 ? (
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--dim)' }}>-</div>
      ) : (
        people.map((p) => {
          const isMe = !!p.userId && p.userId === meId
          const label = participantState(p, status)
          return (
            <div key={p.id} style={{ marginTop: 2 }}>
              <div style={{ fontSize: 15, fontWeight: 700 }}>{nameOf(p, meId)}</div>
              <div
                style={{
                  fontSize: 11, marginTop: 4,
                  color: isMe ? 'var(--bright)' : 'var(--dim)',
                }}
              >
                {isMe ? '✓ you' : label}
              </div>
            </div>
          )
        })
      )}
    </div>
  )
}

function SettleRow({
  person, meId, busy, canMark, onMark,
}: {
  person: ChallengeParticipant
  meId: string
  busy: boolean
  canMark: boolean
  onMark: () => void
}) {
  const done = person.settledAt !== null
  const who = nameOf(person, meId)

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 13, color: done ? 'var(--fg)' : 'var(--dim)' }}>
        {done ? (
          <>
            ✓ {who} did them
            {person.settledSelf ? ' · self-reported' : person.settledByName ? ` · confirmed by ${person.settledByName}` : ''}
          </>
        ) : (
          <>☐ Not yet done{person.isFreeform ? ' · not on the app' : ''}</>
        )}
      </div>
      {!done && canMark ? (
        <button
          onClick={onMark}
          disabled={busy}
          style={{
            display: 'block', width: '100%', marginTop: 12, textAlign: 'center',
            padding: 12, borderRadius: 12, minHeight: 44,
            border: '1px solid var(--line)', fontSize: 13, fontWeight: 600, color: 'var(--bright)',
            opacity: busy ? 0.6 : 1,
          }}
        >
          {person.userId === meId ? 'I did them' : `${who} did them`}
        </button>
      ) : null}
    </div>
  )
}

// ── the eight statuses ──────────────────────────────────────────────────────

function StatusBlock({
  challenge: c, meId, now, busy,
  onAccept, onDecline, onConfirm, onDispute, onResolve, onVoid, onProposeAgain,
}: {
  challenge: Challenge
  meId: string
  now: number
  busy: boolean
  onAccept: () => void
  onDecline: () => void
  onConfirm: () => void
  onDispute: () => void
  onResolve: (outcome: ChallengeOutcome) => void
  onVoid: () => void
  onProposeAgain: () => void
}) {
  const headline = (text: string) => (
    <div style={{ fontSize: 15, fontWeight: 700, marginTop: 10 }}>{text}</div>
  )
  const detail = (text: string) => (
    <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 6 }}>{text}</div>
  )
  const mono = (text: string) => (
    <div className="mono" style={{ fontSize: 11, color: 'var(--dim)', marginTop: 10 }}>{text}</div>
  )

  const winnersSide = c.outcome === 'side_a' ? c.sides.a : c.outcome === 'side_b' ? c.sides.b : []
  const winnerNames = namesOf(winnersSide, meId)

  switch (c.status) {
    // ── 1. proposed ───────────────────────────────────────────────────────
    case 'proposed': {
      // Past its lock time but never flipped by the scheduler yet — same view.
      if (c.locksAt !== null && c.locksAt <= now) {
        return (
          <>
            {headline('Expired before everyone accepted')}
            {detail('It reached its lock time while somebody still hadn’t answered. Nobody owes anything.')}
            {c.viewer.isParticipant ? (
              <div style={{ marginTop: 14 }}>
                <Button variant="secondary" compact onClick={onProposeAgain}>Propose again</Button>
              </div>
            ) : null}
          </>
        )
      }
      if (c.viewer.needsAccept) {
        const pending = [...c.sides.a, ...c.sides.b]
          .filter((p) => p.userId && p.userId !== meId && !p.acceptedAt && !p.declinedAt)
        return (
          <>
            {headline('Waiting on you')}
            {detail(pending.length
              ? `${namesOf(pending, meId)} still ${pending.length === 1 ? 'has' : 'have'} to answer too.`
              : 'Accept and it’s live.')}
            <ButtonPair
              primaryLabel="Accept"
              secondaryLabel="Decline"
              onPrimary={onAccept}
              onSecondary={onDecline}
              busy={busy}
            />
            {c.locksAt !== null ? mono(`Locks in ${countdown(c.locksAt - now)}`) : null}
          </>
        )
      }
      const waiting = [...c.sides.a, ...c.sides.b].filter((p) => p.userId && !p.acceptedAt && !p.declinedAt)
      return (
        <>
          {headline(waiting.length ? `Awaiting ${namesOf(waiting, meId)}` : 'Awaiting acceptance')}
          {detail(c.viewer.isParticipant
            ? 'You’re in. It goes live when everyone has answered.'
            : 'Nothing to do from here. This one isn’t yours.')}
          {c.locksAt !== null ? mono(`Locks in ${countdown(c.locksAt - now)}`) : null}
          {c.creatorId === meId ? (
            <div style={{ marginTop: 14 }}>
              <Button variant="tertiary" compact disabled={busy} onClick={onVoid}>Call it off</Button>
            </div>
          ) : null}
        </>
      )
    }

    // ── 2. accepted ───────────────────────────────────────────────────────
    case 'accepted': {
      const locked = c.isLocked
      return (
        <>
          {headline(locked ? 'Live, waiting on the outcome' : 'Locked in')}
          {detail(locked
            ? c.resolutionSrc === null && c.templateId !== 'custom'
              ? '⚡ This one resolves itself from the live feed.'
              : 'Somebody has to call it when it’s settled.'
            : 'Everyone has accepted. Nothing to do until it locks.')}
          {c.locksAt !== null && !locked ? mono(`Locks in ${countdown(c.locksAt - now)}`) : null}

          {c.viewer.canResolve ? (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 12, color: 'var(--dim)', marginBottom: 8 }}>How did it land?</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button compact style={{ flex: 1 }} disabled={busy} onClick={() => onResolve('side_a')}>
                  {namesOf(c.sides.a, meId)} won
                </Button>
                <Button compact variant="secondary" style={{ flex: 1 }} disabled={busy} onClick={() => onResolve('side_b')}>
                  {namesOf(c.sides.b, meId)} won
                </Button>
              </div>
              <Spacer h={8} />
              <Button compact variant="tertiary" disabled={busy} onClick={() => onResolve('push')}>
                It was a push
              </Button>
            </div>
          ) : null}

          {!locked && c.creatorId === meId ? (
            <div style={{ marginTop: 14 }}>
              <Button variant="tertiary" compact disabled={busy} onClick={onVoid}>Call it off</Button>
            </div>
          ) : null}
        </>
      )
    }

    // ── 3. declined ───────────────────────────────────────────────────────
    case 'declined': {
      const decliner = [...c.sides.a, ...c.sides.b].find((p) => p.declinedAt)
      return (
        <>
          {headline(`${decliner ? nameOf(decliner, meId) : 'Someone'} declined`)}
          {detail('Nobody owes anything. You can propose it again with different terms.')}
          <div style={{ marginTop: 14 }}>
            <Button variant="secondary" compact onClick={onProposeAgain}>Propose again</Button>
          </div>
        </>
      )
    }

    // ── 4. expired ────────────────────────────────────────────────────────
    case 'expired':
      return (
        <>
          {headline('Nobody responded before it locked')}
          {detail('The moment passed while it was still waiting. Nobody owes anything.')}
          {c.viewer.isParticipant ? (
            <div style={{ marginTop: 14 }}>
              <Button variant="secondary" compact onClick={onProposeAgain}>Propose again</Button>
            </div>
          ) : null}
        </>
      )

    // ── 5. resolved ───────────────────────────────────────────────────────
    case 'resolved': {
      const auto = c.resolutionSrc === 'auto'
      const title = c.outcome === 'push'
        ? 'Push. Nobody owes anything'
        : c.outcome === 'void'
          ? 'Voided. The game was called off'
          : auto
            ? '⚡ Auto-resolved from the live feed'
            : `${c.resolvedByName ?? 'Someone'} called it`

      const sentence = c.outcome === 'push'
        ? 'It landed exactly on the line, so the stake is off.'
        : c.outcome === 'void'
          ? 'There is no result to read, so nothing carries.'
          : `${winnerNames} ${winnersSide.length === 1 && winnersSide[0].userId === meId ? 'win' : 'wins'}.`

      return (
        <>
          {headline(title)}
          {detail(sentence)}

          {c.viewer.isParticipant && !c.confirmedAt ? (
            <ButtonPair
              primaryLabel={auto ? 'Looks right' : 'Confirm'}
              secondaryLabel="Dispute"
              onPrimary={onConfirm}
              onSecondary={onDispute}
              busy={busy}
              disabled={!c.viewer.needsConfirm && !auto}
            />
          ) : null}

          {c.confirmDueAt !== null && !c.confirmedAt
            ? mono(`Auto-confirms in ${countdown(c.confirmDueAt - now)}`)
            : c.confirmedAt
              ? mono(`Confirmed ${STAMP.format(c.confirmedAt)}`)
              : null}
        </>
      )
    }

    // ── 6. disputed ───────────────────────────────────────────────────────
    case 'disputed':
      return (
        <>
          {headline('Disputed')}
          {detail(c.outcome === 'side_a' || c.outcome === 'side_b'
            ? `The call was ${winnerNames}. Somebody disagrees.`
            : 'The call is being argued about.')}
          {c.disputeNote ? (
            <div
              style={{
                fontSize: 13, marginTop: 10, padding: '12px 14px',
                borderRadius: 12, border: '1px solid var(--line)', color: 'var(--fg)',
              }}
            >
              “{c.disputeNote}”
            </div>
          ) : null}
          {detail('The app doesn’t adjudicate. An admin will settle this, or you two will.')}
        </>
      )

    // ── 7. settled ────────────────────────────────────────────────────────
    case 'settled':
      return (
        <>
          {headline('Settled')}
          {detail(c.outcome === 'push' || c.outcome === 'void'
            ? 'Nobody owed anything on this one.'
            : `${winnerNames} won, and everything owed has been marked done.`)}
          {c.resolvedAt ? mono(`Resolved ${STAMP.format(c.resolvedAt)}${c.resolutionSrc === 'auto' ? ' · from the live feed' : c.resolvedByName ? ` · by ${c.resolvedByName}` : ''}`) : null}
        </>
      )

    // ── 8. voided ─────────────────────────────────────────────────────────
    case 'voided':
      return (
        <>
          {headline('Voided')}
          {detail(c.adminOverride
            ? 'An admin called this one off. Nobody owes anything.'
            : `${c.creatorName} called this one off. Nobody owes anything.`)}
          {mono('A challenge is never deleted. The record stays.')}
        </>
      )
  }
}
